import React, { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { observer } from "mobx-react";
import { sprintf } from "sprintf-js";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  HStack,
  Input,
  Link,
  SimpleGrid,
  Stack,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import type { OrderDetails } from "@/store/types/order";
import { AddressLookupSelect } from "@/components/AddressLookupSelect";
import { ProvinceSelect } from "@/pages/Indigenous/Portal/ProvinceSelect";
import { provinceFullNameById } from "@/store/types/province";
import type { ProvinceFull } from "@/store/types/province";
import { OrderSummaryCard } from "../Summary/OrderSummaryCard";
import type { AddressLookupDetailsDto } from "@/api/types";
import { getEmailPattern } from "@/helpers/form";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { stripColon } from "@/helpers/string";
import { H3 } from "@/components/Heading";
import { FormInput } from "@/components/Form/FormInput";
import { FormGroup } from "@/components/Form/FormGroup";
import { FormFieldError } from "@/components/Form/FormFieldError";
import { MdError } from "react-icons/md";
import { otherOrganizationType } from "@/constants/order";
import { OrganizationTypeSelect } from "./OrganizationTypeSelect";
import { telemetry } from "@/telemetry";
import type { AddressKind } from "@/telemetry/events";

export const OrderShippingDetailsBasePanel = observer(() => {
  const {
    activeLanguage,
    activeProvinceTranslated,
    activeProvinceFullName,
    isEmergentFlow,
    orderData,
    setOrderData,
    getErrorInputText,
    ga4Store,
  } = useStore();
  const language = translations.pages["order-summary"][activeLanguage];
  const commonLanguage = translations.common["provinces-fullName"][
    activeLanguage
  ] as any;
  const errorTranslations = translations.common.errors[activeLanguage];
  const navigate = useAppNavigate();
  const lookupSearches = useRef({ billing: 0, shipping: 0 });

  const onSubmit = (values: OrderDetails) => {
    if (!isProvinceMatch()) {
      trackValidationFailed({});
      return;
    }

    setOrderData({
      ...orderData,
      ...values,
    });
    telemetry.trackEvent("shipping_submitted", {
      same_as_billing: values.sameAsBilling,
      manual_address: !!values.isManualAddress,
      save_shipping: values.saveShippingAddress,
      save_billing: values.saveBillingAddress,
    });
    ga4Store.addShippingInfo();
    navigate({
      to: "/order/summary",
    });
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    control,
    setValue,
    getValues,
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: orderData,
  });

  React.useEffect(() => {
    register("isManualAddress");
  }, [register]);

  const handleShippingLookupSelect = (data: AddressLookupDetailsDto) => {
    setValue("shippingCity", data.city);
    setValue("shippingProvince", data.province);
    setValue("shippingPostalCode", data.postalCode);
    setValue("shippingStreetAddress", data.text);
  };

  const handleBillingLookupSelect = (data: AddressLookupDetailsDto) => {
    setValue("billingCity", data.city);
    setValue("billingProvince", data.province);
    setValue("billingPostalCode", data.postalCode);
    setValue("billingStreetAddress", data.text);

    if (watch("sameAsBilling")) {
      handleShippingLookupSelect(data);
    }
  };

  const isSameAsBilling = watch("sameAsBilling");
  const isManualAddress = watch("isManualAddress");

  const countLookupSearch = (address: AddressKind) => {
    lookupSearches.current[address] += 1;
  };

  const handleManualAddressToggle = (address: AddressKind) => {
    if (!isManualAddress)
      telemetry.trackEvent("manual_address_toggled", {
        address,
        searches_before_switching: lookupSearches.current[address],
      });

    setValue("isManualAddress", !isManualAddress, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const renderManualAddressToggle = (address: AddressKind) => (
    <Link
      as="button"
      type="button"
      alignSelf="flex-start"
      bg="transparent"
      border="0"
      color="gray.400"
      fontWeight="700"
      lineHeight="1.2"
      fontSize="12px"
      p="0"
      onClick={() => handleManualAddressToggle(address)}
    >
      {isManualAddress
        ? language.use_address_lookup
        : language.manual_address_lookup}
    </Link>
  );

  const handleSameAsBilling = () => {
    const values = getValues();

    if (values.sameAsBilling) {
      setValue("shippingName", values.billingName);
      setValue("shippingBusinessName", values.billingBusinessName);
      setValue("shippingStreetAddress", values.billingStreetAddress);
      setValue("shippingOptionalUnit", values.billingOptionalUnit);
      setValue("shippingCity", values.billingCity);
      setValue("shippingProvince", values.billingProvince);
      setValue("shippingPostalCode", values.billingPostalCode);
    } else {
      setValue("shippingName", "");
      setValue("shippingBusinessName", "");
      setValue("shippingStreetAddress", "");
      setValue("shippingOptionalUnit", "");
      setValue("shippingCity", "");
      setValue("shippingProvince", "");
      setValue("shippingPostalCode", "");
    }
  };

  const shippingProvince = watch("shippingProvince");
  const billingProvince = watch("billingProvince");

  const isProvinceMatch = () => {
    const values = getValues();

    if (isEmergentFlow) return true;

    return values.shippingProvince === activeProvinceFullName;
  };

  const showProvinceMismatchError = !!shippingProvince && !isProvinceMatch();

  const trackValidationFailed = (formErrors: FieldErrors<OrderDetails>) => {
    const failedFields = Object.keys(formErrors);

    if (!isProvinceMatch() && !failedFields.includes("shippingProvince"))
      failedFields.push("shippingProvince");

    telemetry.trackEvent("shipping_validation_failed", {
      failed_fields: failedFields.join(","),
      count: failedFields.length,
    });
  };

  const isOtherOrganizationType =
    watch("organizationType") === otherOrganizationType;
  const isOrganizationTypeValid =
    !isOtherOrganizationType || !!watch("organizationTypeOther");

  const isFormValid = isValid && isProvinceMatch() && isOrganizationTypeValid;

  const billingProvinceLabel =
    commonLanguage[billingProvince] || billingProvince;

  const shippingProvinceLabel =
    commonLanguage[shippingProvince] || shippingProvince;

  return (
    <Stack
      as="form"
      spacing="14"
      onSubmit={handleSubmit(onSubmit, trackValidationFailed)}
    >
      <HStack
        alignItems="flex-start"
        spacing="14"
        flexDirection={{ base: "column", lg: "row" }}
      >
        <Stack
          spacing="14"
          flex="1"
          width="100%"
        >
          {orderData.ada && (
            <HStack>
              <H3>{language.emergent_direct_account_ada}</H3>
              <H3>{orderData.ada}</H3>
            </HStack>
          )}

          <Stack spacing="8">
            <H3>{language.enter_contact_info}</H3>
            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacing="6"
              alignItems="flex-start"
            >
              <FormInput
                label={language.phone_no}
                placeholder="1-(xxx)-xxx-xxx"
                error={errors.phoneNumber}
                {...register("phoneNumber", {
                  required: true,
                })}
              />
              <Stack spacing="6">
                <FormGroup
                  label={language.organization_type}
                  error={errors.organizationType}
                >
                  <Controller
                    name="organizationType"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <OrganizationTypeSelect
                        value={value}
                        onSelect={(v) => {
                          onChange(v);

                          if (v !== otherOrganizationType) {
                            setValue("organizationTypeOther", "", {
                              shouldValidate: true,
                            });
                          }
                        }}
                        placeholder={stripColon(language.organization_type)}
                      />
                    )}
                    rules={{ required: true }}
                  />
                </FormGroup>
                {isOtherOrganizationType && (
                  <FormInput
                    label={language.organization_type_other}
                    error={errors.organizationTypeOther}
                    {...register("organizationTypeOther", {
                      setValueAs: (value: string) => value?.trim() || "",
                      validate: (value, values) =>
                        values.organizationType !== otherOrganizationType ||
                        !!value ||
                        errorTranslations.organization_type_other_required,
                    })}
                  />
                )}
              </Stack>
            </SimpleGrid>
            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacing="6"
            >
              <FormInput
                label={language.email_address}
                error={errors.emailAddress}
                {...register("emailAddress", {
                  pattern: getEmailPattern(
                    errorTranslations.email_invalid_format,
                  ),
                  required: true,
                })}
              />
              <FormInput
                label={language.confirm_email_address}
                error={errors.confirmEmailAddress}
                {...register("confirmEmailAddress", {
                  pattern: getEmailPattern(
                    errorTranslations.email_invalid_format,
                  ),
                  required: true,
                  validate: (value, values) =>
                    value === values.emailAddress ||
                    errorTranslations.emails_must_match,
                })}
              />
            </SimpleGrid>
          </Stack>

          <Stack spacing="8">
            <H3>{language.enter_billing_info}</H3>
            <Stack spacing="4">
              <FormInput
                label={language.name}
                error={errors.billingName}
                {...register("billingName", {
                  required: true,
                })}
              />
              <FormInput
                label={
                  isEmergentFlow
                    ? language.business_name
                    : language.business_name_optional
                }
                {...register("billingBusinessName", {
                  required: isEmergentFlow,
                })}
              />
              <Stack spacing="2">
                {isManualAddress ? (
                  <FormInput
                    label={language.street_address}
                    error={errors.billingStreetAddress}
                    {...register("billingStreetAddress", {
                      required: getErrorInputText(language.street_address),
                    })}
                  />
                ) : (
                  <FormGroup
                    label={language.street_address}
                    error={errors.billingStreetAddress}
                  >
                    <Controller
                      name="billingStreetAddress"
                      control={control}
                      render={({ field: { onBlur, onChange, value } }) => (
                        <AddressLookupSelect
                          name="billingStreetAddress"
                          onSearch={() => countLookupSearch("billing")}
                          onSelect={(data) => {
                            handleBillingLookupSelect(data);
                            onChange(data.text);
                          }}
                          onBlur={onBlur}
                          value={value}
                          placeholder={language.street_address}
                        />
                      )}
                      rules={{
                        required: getErrorInputText(language.street_address),
                      }}
                    />
                  </FormGroup>
                )}
                {renderManualAddressToggle("billing")}
              </Stack>

              <SimpleGrid
                columns={{ sm: 1, md: 2 }}
                spacing="6"
              >
                <FormInput
                  label={language.unit_suit}
                  {...register("billingOptionalUnit")}
                />
                <FormInput
                  label={language.city}
                  isReadOnly={!isManualAddress}
                  {...register("billingCity")}
                />
                {isManualAddress ? (
                  <FormGroup
                    label={
                      isEmergentFlow
                        ? language.province_territory
                        : language.province
                    }
                    error={errors.billingProvince}
                  >
                    <Controller
                      name="billingProvince"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <ProvinceSelect
                          items={Object.values(provinceFullNameById)}
                          value={value}
                          onSelect={(v) => onChange(v as ProvinceFull)}
                          placeholder={
                            isEmergentFlow
                              ? language.province_territory
                              : language.province
                          }
                        />
                      )}
                      rules={{ required: true }}
                    />
                  </FormGroup>
                ) : (
                  <FormInput
                    label={
                      isEmergentFlow
                        ? language.province_territory
                        : language.province
                    }
                    isReadOnly
                    value={billingProvinceLabel}
                  />
                )}
                <FormInput
                  label={language.postal_code}
                  isReadOnly={!isManualAddress}
                  {...register("billingPostalCode")}
                />
              </SimpleGrid>
            </Stack>
          </Stack>

          {isEmergentFlow && (
            <Stack spacing="8">
              <H3>{language.enter_order_reference_number}</H3>
              <Input
                placeholder={stripColon(language.order_reference_number)}
                {...register("orderReference")}
              />
            </Stack>
          )}

          <Stack spacing="8">
            <H3>{language.enter_shipping_info}</H3>
            <Checkbox
              {...register("sameAsBilling", {
                onChange: handleSameAsBilling,
              })}
            >
              {language.same_as_billing}
            </Checkbox>
            <FormInput
              label={language.shipping_name}
              isReadOnly={isSameAsBilling}
              error={errors.shippingName}
              {...register("shippingName", {
                required: true,
              })}
            />
            <FormInput
              label={
                isEmergentFlow
                  ? language.shipping_business_name
                  : language.shipping_business_name_optional
              }
              isReadOnly={isSameAsBilling}
              {...register("shippingBusinessName", {
                required: isEmergentFlow,
              })}
            />
            <Stack spacing="2">
              {isManualAddress ? (
                <FormInput
                  label={language.shipping_street_address}
                  error={errors.shippingStreetAddress}
                  isReadOnly={isSameAsBilling}
                  {...register("shippingStreetAddress", {
                    required: getErrorInputText(
                      language.shipping_street_address,
                    ),
                  })}
                />
              ) : (
                <FormGroup
                  label={language.shipping_street_address}
                  error={errors.shippingStreetAddress}
                >
                  <Controller
                    name="shippingStreetAddress"
                    control={control}
                    render={({ field: { onBlur, onChange, value } }) => (
                      <AddressLookupSelect
                        name="shippingStreetAddress"
                        onSearch={() => countLookupSearch("shipping")}
                        onSelect={(data) => {
                          handleShippingLookupSelect(data);
                          onChange(data.text);
                        }}
                        onBlur={onBlur}
                        value={value}
                        placeholder={language.shipping_street_address}
                      />
                    )}
                    rules={{
                      required: getErrorInputText(
                        language.shipping_street_address,
                      ),
                    }}
                  />
                </FormGroup>
              )}
              {renderManualAddressToggle("shipping")}
            </Stack>

            <Stack spacing="4">
              <SimpleGrid
                columns={{ sm: 1, md: 2 }}
                spacing="6"
              >
                <FormInput
                  label={language.shipping_unit_suit}
                  {...register("shippingOptionalUnit")}
                />
                <FormInput
                  isReadOnly={!isManualAddress}
                  label={language.shipping_city}
                  error={errors.shippingCity}
                  {...register("shippingCity", {
                    required: true,
                  })}
                />
                {isManualAddress ? (
                  <Box position="relative">
                    {showProvinceMismatchError && (
                      <Box
                        position="absolute"
                        top="10"
                        left="-8"
                        color="primary-light"
                      >
                        <MdError size="24" />
                      </Box>
                    )}
                    <FormGroup
                      label={
                        isEmergentFlow
                          ? language.province_territory
                          : language.province
                      }
                      error={errors.shippingProvince}
                      isError={showProvinceMismatchError}
                    >
                      <Controller
                        name="shippingProvince"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <ProvinceSelect
                            items={Object.values(provinceFullNameById)}
                            value={value}
                            onSelect={(v) => onChange(v as ProvinceFull)}
                            placeholder={
                              isEmergentFlow
                                ? language.province_territory
                                : language.province
                            }
                          />
                        )}
                        rules={{ required: true }}
                      />
                    </FormGroup>
                  </Box>
                ) : (
                  <Box position="relative">
                    {showProvinceMismatchError && (
                      <Box
                        position="absolute"
                        top="10"
                        left="-8"
                        color="primary-light"
                      >
                        <MdError size="24" />
                      </Box>
                    )}
                    <FormInput
                      label={
                        isEmergentFlow
                          ? language.province_territory
                          : language.province
                      }
                      isError={showProvinceMismatchError}
                      value={shippingProvinceLabel}
                    />
                  </Box>
                )}
                <FormInput
                  label={language.shipping_postal_code}
                  isReadOnly={!isManualAddress}
                  {...register("shippingPostalCode", {
                    required: true,
                  })}
                />
              </SimpleGrid>
              <FormControl>
                {showProvinceMismatchError && (
                  <FormFieldError
                    error={sprintf(
                      language.province_mismatch_error,
                      activeProvinceTranslated,
                    )}
                  />
                )}
              </FormControl>
            </Stack>
          </Stack>
        </Stack>
        <OrderSummaryCard isFinalCost={false} />
      </HStack>

      <HStack justifyContent="flex-end">
        <Button
          aria-disabled={!isFormValid}
          type="submit"
        >
          {language.review_details}
        </Button>
      </HStack>
    </Stack>
  );
});
